// Auto-generated by scripts/split_calculators.py — keeps deterministic oilfield formulas in per-category modules.
import { round, num, positive, toRad } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive, toRad };

// ===== Category: mud =====
// --- Mud & Fluids ---
// =========================================================================
// MUD & FLUIDS
// =========================================================================

export function bariteRequired({ currentMW, desiredMW, volumeBbl }) {
  const W1 = positive(num(currentMW, "Current MW"), "Current MW");
  const W2 = positive(num(desiredMW, "Desired MW"), "Desired MW");
  if (W2 <= W1) throw new Error("Desired MW must be greater than current MW");
  if (W2 >= 35) throw new Error("Desired MW must be less than 35 ppg (barite density)");
  const V = positive(num(volumeBbl, "System Volume"), "System Volume");
  const per100 = 1470 * (W2 - W1) / (35 - W2);
  const totalSx = per100 * (V / 100);
  return {
    result: round(totalSx, 0),
    unit: "sx (100-lb)",
    formula: "Barite (sx/100bbl) = 1470 × (W2 − W1) / (35 − W2)",
    steps: [
      `W1=${W1}, W2=${W2}, V=${V} bbl`,
      `sx/100bbl = 1470 × (${W2}−${W1}) / (35−${W2}) = ${round(per100, 1)}`,
      `Total = ${round(per100, 1)} × (${V}/100) = ${round(totalSx, 0)} sx`,
    ],
    inputs: { "Current MW": W1, "Desired MW": W2, "Volume (bbl)": V },
    extras: { "Sacks per 100 bbl": round(per100, 1) },
  };
}

export function dilutionVolume({ currentMW, desiredMW, dilutionFluidMW, volumeBbl }) {
  const W1 = positive(num(currentMW, "Current MW"), "Current MW");
  const W2 = positive(num(desiredMW, "Desired MW"), "Desired MW");
  const Wf = num(dilutionFluidMW, "Dilution Fluid MW"); if (Wf < 0) throw new Error("Dilution fluid MW must be ≥ 0");
  const V = positive(num(volumeBbl, "Current Volume"), "Current Volume");
  if (W2 >= W1) throw new Error("Desired MW must be less than current MW");
  if (Wf >= W2) throw new Error("Dilution fluid MW must be less than desired MW");
  const Vd = V * (W1 - W2) / (W2 - Wf);
  return {
    result: round(Vd, 2),
    unit: "bbl",
    formula: "V_add = V × (W1 − W2) / (W2 − Wf)",
    steps: [
      `V=${V} bbl, W1=${W1}, W2=${W2}, Wf=${Wf}`,
      `V_add = ${V} × (${W1}−${W2}) / (${W2}−${Wf})`,
      `V_add = ${round(Vd, 2)} bbl`,
    ],
    inputs: { "Volume (bbl)": V, "Current MW": W1, "Desired MW": W2, "Dilution Fluid MW": Wf },
  };
}

export function mudWeightIncrease(args) {
  // Alias to barite required
  return bariteRequired(args);
}

export function mudWeightReduction({ currentMW, desiredMW, dilutionFluidMW = 8.33, volumeBbl }) {
  return dilutionVolume({ currentMW, desiredMW, dilutionFluidMW, volumeBbl });
}

export function volumeIncreaseWeightUp({ currentMW, desiredMW, volumeBbl }) {
  const W1 = positive(num(currentMW, "Current MW"), "Current MW");
  const W2 = positive(num(desiredMW, "Desired MW"), "Desired MW");
  if (W2 <= W1) throw new Error("Desired MW must be greater than current MW");
  if (W2 >= 35) throw new Error("Desired MW must be less than 35 ppg");
  const V = positive(num(volumeBbl, "System Volume"), "System Volume");
  const per100 = 100 * (W2 - W1) / (35 - W2);
  const totalInc = per100 * (V / 100);
  return {
    result: round(totalInc, 2),
    unit: "bbl",
    formula: "ΔV (bbl/100bbl) = 100 × (W2 − W1) / (35 − W2)",
    steps: [
      `bbl/100bbl = 100 × (${W2}−${W1})/(35−${W2}) = ${round(per100, 2)}`,
      `Total ΔV = ${round(per100, 2)} × (${V}/100) = ${round(totalInc, 2)} bbl`,
    ],
    inputs: { "Current MW": W1, "Desired MW": W2, "Volume (bbl)": V },
    extras: { "ΔV per 100 bbl": round(per100, 2) },
  };
}

// --- v1.4 — Mud additions ---
// ----- Mud additions -----

export function chemicalConcentration({ concentration, systemVolume }) {
  const C = positive(num(concentration, "Concentration"), "Concentration");
  const V = positive(num(systemVolume, "System Volume"), "System Volume");
  const need = C * V;
  return {
    result: round(need, 3),
    unit: "chemical units",
    formula: "Chemical Required = Desired Concentration × System Volume",
    steps: [`${C} × ${V} = ${round(need, 3)} chemical units (use consistent units)`],
    inputs: { "Concentration": C, "System Volume": V },
  };
}

export function oilWaterRatio({ oilVolume, waterVolume }) {
  const O = num(oilVolume, "Oil Volume"); if (O < 0) throw new Error("Oil must be ≥ 0");
  const W = num(waterVolume, "Water Volume"); if (W < 0) throw new Error("Water must be ≥ 0");
  const total = O + W;
  if (total === 0) throw new Error("Oil + water must be > 0");
  const oilPct = (O / total) * 100;
  const waterPct = (W / total) * 100;
  return {
    result: round(oilPct, 1),
    unit: "% oil",
    formula: "Oil% = Oil / (Oil+Water) × 100;  Water% = 100 − Oil%",
    steps: [
      `Total liquid = ${O} + ${W} = ${total}`,
      `Oil% = ${O}/${total} × 100 = ${round(oilPct, 1)}%`,
      `Water% = ${round(waterPct, 1)}%`,
      `OWR (oil:water) = ${round(oilPct, 0)}:${round(waterPct, 0)}`,
    ],
    inputs: { "Oil Volume": O, "Water Volume": W },
    extras: { "Water (%)": round(waterPct, 1), "OWR": `${round(oilPct, 0)}:${round(waterPct, 0)}` },
  };
}

export function retortSolids({ oilRetortPct, waterRetortPct }) {
  const O = num(oilRetortPct, "Oil %"); if (O < 0 || O > 100) throw new Error("Oil % must be 0–100");
  const W = num(waterRetortPct, "Water %"); if (W < 0 || W > 100) throw new Error("Water % must be 0–100");
  if (O + W > 100) throw new Error("Oil + Water cannot exceed 100%");
  const solids = 100 - O - W;
  return {
    result: round(solids, 2),
    unit: "% solids",
    formula: "Solids% = 100 − Oil% − Water%   (from retort)",
    steps: [`Solids% = 100 − ${O} − ${W} = ${round(solids, 2)}%`],
    inputs: { "Oil (%)": O, "Water (%)": W },
    extras: { "Oil (%)": O, "Water (%)": W, "Solids (%)": round(solids, 2) },
  };
}

export function brineDensityConvert({ fromSystem, value }) {
  // Convert a single density value to ppg, SG, kg/m³
  const v = positive(num(value, "Value"), "Value");
  const f = (fromSystem || "ppg").toLowerCase();
  let ppg;
  if (f === "ppg") ppg = v;
  else if (f === "sg") ppg = v * 8.345;
  else if (f === "kg/m3" || f === "kgm3") ppg = v / 119.826;
  else throw new Error("Unknown 'from' unit (use ppg, sg, kg/m3)");
  const sg = ppg / 8.345;
  const kgm3 = ppg * 119.826;
  return {
    result: round(ppg, 3),
    unit: "ppg",
    formula: "1 SG = 8.345 ppg ;  1 ppg = 119.826 kg/m³",
    steps: [
      `Input = ${v} ${f}`,
      `→ ${round(ppg, 3)} ppg`,
      `→ ${round(sg, 4)} SG`,
      `→ ${round(kgm3, 1)} kg/m³`,
    ],
    inputs: { "From": f, "Value": v },
    extras: { "ppg": round(ppg, 3), "SG": round(sg, 4), "kg/m³": round(kgm3, 1) },
  };
}

export function mudCostPerBarrel({ totalCost, totalBarrels }) {
  const C = positive(num(totalCost, "Total Mud Cost"), "Total Mud Cost");
  const B = positive(num(totalBarrels, "Total Barrels"), "Total Barrels");
  const r = C / B;
  return {
    result: round(r, 2),
    unit: "$/bbl",
    formula: "Cost/bbl = Total Cost / Total Barrels",
    steps: [`$${C} / ${B} bbl = $${round(r, 2)} /bbl`],
    inputs: { "Total Cost": C, "Total Barrels": B },
  };
}


